import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCheck, MessageSquare, ShoppingCart } from 'lucide-react';
import { useMessages } from '../stores/messages.js';
import { formatRelativeParis } from '../lib/dates.js';
import { Button } from '../ui/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { ListSkeleton } from '../components/TaskList.js';
import { teinte } from '../lib/teinte.js';
import { cn } from '../lib/cn.js';
import { texteBrut } from '../lib/linkify.js';
import type { Message, MessageSource } from '../api/messages.js';

/**
 * La boîte de réception des notifications (ADR 0008). Atteinte par la cloche, jamais par
 * la nav : ce n'est pas une destination quotidienne, c'est un accusé de réception.
 */
export function Messages(): React.ReactElement {
  const items = useMessages((s) => s.items);
  const cursor = useMessages((s) => s.cursor);
  const unread = useMessages((s) => s.unread);
  const loading = useMessages((s) => s.loading);
  const loadingMore = useMessages((s) => s.loadingMore);
  const load = useMessages((s) => s.load);
  const loadMore = useMessages((s) => s.loadMore);
  const markAllRead = useMessages((s) => s.markAllRead);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="max-w-2xl mx-auto w-full px-4 md:px-6 py-4 md:py-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="hidden md:block text-2xl">Messages</h1>
        <Button
          variant="secondary"
          size="small"
          onClick={() => markAllRead()}
          disabled={unread === 0}
          className="md:ml-auto"
        >
          <CheckCheck size={16} aria-hidden />
          Tout marquer lu
        </Button>
      </div>

      {items.length === 0 ? (
        loading ? (
          <ListSkeleton />
        ) : (
          <EmptyState>
            Aucun message. Les rappels et les messages envoyés depuis Claude arriveront ici.
          </EmptyState>
        )
      ) : (
        <div className="liste liste-aeree">
          {items.map((m) => (
            <MessageRow key={m.messageId} message={m} />
          ))}
        </div>
      )}

      {cursor && (
        <div className="flex justify-center pt-2">
          <Button variant="ghost" size="small" onClick={() => loadMore()} loading={loadingMore}>
            Charger plus
          </Button>
        </div>
      )}
    </div>
  );
}

// Partial, and read through a fallback: messages written by a source since retired (morning,
// evening — ADR 0009) stay in the box until their TTL, and a source can ship server-side before
// the web knows about it.
const SOURCE_ICON: Partial<Record<MessageSource, React.ReactNode>> = {
  carrefour: <ShoppingCart size={16} aria-hidden />,
  external: <MessageSquare size={16} aria-hidden />,
};

// `.ligne-bordee` du kit : un message est un objet qu'on ouvre. Non lu, il porte un point de
// marque ET un titre en gras — la couleur n'est jamais le seul signal. Toute la ligne est un
// lien : titre et corps y sont en texte brut (pas de `<a>` dans un `<a>`), le détail les lie.
export function MessageRow({ message }: { message: Message }): React.ReactElement {
  return (
    <Link to={`/messages/${message.messageId}`} className="ligne ligne-bordee ligne-cliquable">
      <span
        className={cn('point', message.read && 'invisible')}
        style={teinte('var(--fond-marque)')}
        role="img"
        aria-label={message.read ? undefined : 'Non lu'}
        aria-hidden={message.read || undefined}
      />

      <span className="shrink-0 text-muted">
        {SOURCE_ICON[message.source] ?? <MessageSquare size={16} aria-hidden />}
      </span>

      <span className="ligne-corps">
        <span className={cn('ligne-titre', !message.read && 'font-semibold')}>{texteBrut(message.title)}</span>
        <span className="ligne-detail">{texteBrut(message.body).replace(/\s+/g, ' ').trim()}</span>
      </span>

      <span className="ligne-repere">{formatRelativeParis(message.createdAt)}</span>
    </Link>
  );
}
